import Ordenes from './Ordenes';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  api,
  ESTADOS_CAPACHO,
  type DatosPorGrupo,
  type Etiqueta,
  type EtiquetaPlantilla,
  type EstadoCapacho,
  type GrupoDeEtiqueta,
  type LineaDePallet,
  type OrdenEnMesa,
  type PedidosDeEtiqueta,
  type SalidaDeOrden,
  type Unidad,
} from '../api';
import EtiquetaDibujada from '../components/Etiqueta';
import { abrir, aparecer, desplegar, fila, pop, resorte, suave } from '../components/movimiento';
import Escaner from '../components/Escaner';
import { imprimirEtiquetas } from '../lib/imprimir';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useEstadoGuardado } from '../lib/persistencia';
import { useEsAdmin, useSesion } from '../lib/sesion';
import type { Vista } from '../components/Layout';
import { numeroImpreso } from './Remitos';
import { corte, cortePlural } from './Solicitud';
import {
  Cargando,
  EstadoChip,
  Icono,
  Numero,
  PanelError,
  Paginador,
  useConfirmar,
  usePaginado,
} from '../components/ui';
import {
  altoMinimo,
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  chip,
  chipBoton,
  etiquetaCampo,
  fechaImpresa,
  mono,
  punto,
  tarjeta,
  tono,
  tonoDeEstado,
  tonoDeTexto,
  enlace,
  HUSO,
  hoyISO,
} from '../theme';

/**
 * Entrega de a uno los capachos dados. Devuelve '' si salieron todos o el
 * mensaje de falla: si se corta a mitad, cuántos salieron y cuál falló.
 */
async function entregarCapachos(ids: string[]): Promise<string> {
  let hechos = 0;
  try {
    for (const id of ids) {
      await api.moverCapacho(id, 'Entregado');
      hechos++;
    }
    return '';
  } catch (err) {
    return hechos > 0
      ? `Se entregaron ${hechos} de ${ids.length}; falló el ${hechos + 1}º: ${mensajeDeError(err)}`
      : mensajeDeError(err);
  }
}

/* ------------------------------------------------------------------ detalle */

function Detalle({ pallet, onVolver, onIr }: { pallet: string; onVolver: () => void; onIr: (v: Vista, id?: string) => void }) {
  const { vistas } = useSesion();
  // Vacío hasta que se sepa con qué artículo se arma: nunca 45 fijo. Se
  // precarga con `maxPorCapacho` del maestro o, si no está cargado, con la
  // última cantidad que se usó para ese artículo en esta sesión.
  const [cantidad, setCantidad] = useState('');
  const [cuantos, setCuantos] = useState('1');
  const [ultimaCantidad, setUltimaCantidad] = useEstadoGuardado<Record<string, number>>('frac.ultimaCantidad', {});
  // De qué línea del pallet sale el próximo capacho. En un pallet de un solo
  // artículo no hay nada que elegir y se resuelve solo.
  const [linea, setLinea] = useState<number | null>(null);
  const [avisoTexto, setAvisoTexto] = useState('');
  const [pedidos, setPedidos] = useState<PedidosDeEtiqueta | null>(null);
  const [confirmar, dialogo] = useConfirmar();
  const esAdmin = useEsAdmin();

  // Qué hay que cargar a mano para poder emitir la etiqueta, que depende de
  // qué lleve la plantilla. Lo que ya se haya cargado antes viene completado.
  useEffect(() => {
    void api
      .pedidosDeEtiqueta(pallet)
      .then(setPedidos)
      .catch(() => setPedidos(null));
  }, [pallet]);
  const [ocupado, setOcupado] = useState(false);

  // La ficha del pallet también se mantiene sola: dos operarios pueden estar
  // sobre el mismo pallet, y el saldo con el que uno arma un capacho no puede
  // ser el de hace cinco minutos.
  const { datos, cargando, error, recargar } = useApi(() => api.fraccionamiento(pallet), [pallet], VIVO.rapido);
  const capachos = datos?.capachos ?? [];
  const emision = useEmision(capachos, datos?.grupos, pedidos?.cargados);
  const { setEtiquetas } = emision;

  useEffect(() => {
    setEtiquetas(null);
    setAvisoTexto('');
    setLinea(null);
    setCantidad('');
  }, [pallet, setEtiquetas]);

  const items = datos?.items ?? [];
  // El pallet que sale entero se mira acá igual que cualquier otro, pero no
  // hay nada que partir: no lleva el formulario de capachos y su única salida
  // es la del pallet completo.
  const entero = datos?.tipo === 'completo';
  // Con una sola línea no hay elección posible: es esa. Con varias, hasta que
  // el operario no elija no se puede armar nada, porque el capacho sale de un
  // artículo y el sistema no puede adivinar cuál.
  const elegida = linea ?? (items.length === 1 ? items[0]!.id : null);
  const lineaActual = items.find((i) => i.id === elegida) ?? null;

  // La precarga de piezas: el tope del artículo, o si no está cargado el
  // último que se usó para ese artículo en esta sesión, y si tampoco hay,
  // vacío. Nunca más que el saldo de la línea. Se recalcula al elegir línea,
  // no en cada sondeo: si el operario ya escribió algo, un `recargar` de
  // fondo no se lo tiene que pisar.
  useEffect(() => {
    if (!lineaActual) return;
    const tope = lineaActual.maxPorCapacho ?? ultimaCantidad[lineaActual.articulo] ?? null;
    setCantidad(tope ? String(Math.min(tope, lineaActual.saldo)) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineaActual?.id]);

  /**
   * Arma uno o varios capachos iguales, de a uno: el backend no tiene ruta de
   * lote y pedir varios de a uno es lo más simple que funciona. Si corta a
   * mitad de camino, se avisa cuántos salieron y cuál fue el que falló, en vez
   * de tirar el error crudo del que se cortó.
   */
  async function agregar() {
    if (items.length > 1 && elegida === null) {
      setAvisoTexto('El pallet lleva varios artículos: elegí de cuál sale el capacho.');
      return;
    }
    const piezas = Number(cantidad);
    const veces = Math.max(1, Math.trunc(Number(cuantos)) || 1);
    setOcupado(true);
    let hechos = 0;
    try {
      for (let i = 0; i < veces; i++) {
        await api.agregarCapacho(pallet, piezas, elegida ?? undefined);
        hechos++;
      }
      if (lineaActual) setUltimaCantidad((antes) => ({ ...antes, [lineaActual.articulo]: piezas }));
      setAvisoTexto('');
      setEtiquetas(null);
    } catch (err) {
      setAvisoTexto(
        hechos > 0
          ? `Se armaron ${hechos} de ${veces}; falló el ${hechos + 1}º: ${mensajeDeError(err)}`
          : mensajeDeError(err),
      );
    } finally {
      setOcupado(false);
      recargar();
    }
  }

  async function quitar(id: string, detalle?: string) {
    const ok = await confirmar({ titulo: `¿Quitar el capacho ${id}?`, detalle, boton: 'Quitar el capacho' });
    if (!ok) return;
    try {
      await api.quitarCapacho(id);
      setEtiquetas(null);
      recargar();
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    }
  }

  /** Mueve un capacho un paso, en el sentido que se le pida. */
  async function mover(id: string, actual: EstadoCapacho, paso: 1 | -1) {
    const destino = ESTADOS_CAPACHO[ESTADOS_CAPACHO.indexOf(actual) + paso];
    if (!destino) return;
    try {
      await api.moverCapacho(id, destino);
      setAvisoTexto('');
      recargar();
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    }
  }

  /**
   * Acepta un pedido de ZF contra una línea de este pallet: es el momento en
   * que se decide de dónde sale la mercadería.
   */
  async function aceptar(id: string, item: number) {
    try {
      await api.moverCapacho(id, 'Aceptado', item);
      setAvisoTexto('');
      recargar();
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={onVolver} style={{ ...chipBoton(false), padding: '7px 14px' }}>
          Órdenes de trabajo
        </button>
        <Icono nombre="chevron_right" size={18} color={c.texto4} />
        {/* La miga de dónde estás se abre desde el centro, como el resto de lo que llega. */}
        <motion.span
          {...abrir(999)}
          style={{
            padding: '7px 14px',
            borderRadius: 999,
            background: c.verdeSuave,
            border: `1px solid ${c.verde}`,
            color: c.verde,
            fontFamily: mono,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {pallet}
        </motion.span>
      </div>

      {error ? (
        <PanelError mensaje={error} onReintentar={recargar} />
      ) : cargando && !datos ? (
        <Cargando texto="Abriendo el pallet…" />
      ) : (
        datos && (
          // Una columna hasta el escritorio: en la tablet, de a dos, la ficha
          // quedaba en media pantalla y se partían los números y los artículos.
          <div className="grid items-start gap-4 escritorio:grid-cols-[repeat(auto-fit,minmax(320px,1fr))]">
            {/* Las dos tarjetas se abren desde el centro. Ninguna tiene adentro
                nada `fixed` (los diálogos van afuera): el recorte no los toca. */}
            <motion.div {...abrir(16)} style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: c.texto3 }}>
                  Pallet origen
                </div>
                <div style={{ marginTop: 6, fontFamily: mono, fontSize: 18, fontWeight: 500, color: c.texto }}>
                  {datos.pallet}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: c.texto2 }}>
                  {datos.articulo}
                  {datos.lote ? ` · ${datos.lote}` : ''} ·{' '}
                  {vistas.includes('racks') && !/^sin ubicar/i.test(datos.ubicacion) ? (
                    <button type="button" onClick={() => onIr('racks', datos.ubicacion)} title="Ver la posición en el mapa" style={{ ...enlace, fontSize: 13 }}>
                      {datos.ubicacion}
                    </button>
                  ) : (
                    datos.ubicacion
                  )}
                </div>
                <div style={{ marginTop: 3, fontSize: 12.5, color: c.texto3 }}>
                  Contenedor{' '}
                  {datos.contenedor && vistas.includes('contenedor') ? (
                    <button type="button" onClick={() => onIr('contenedor', datos.contenedor!)} title="Ver la ficha del contenedor" style={{ ...enlace, fontSize: 12.5 }}>
                      {datos.contenedor}
                    </button>
                  ) : (
                    <span style={{ fontFamily: mono, color: datos.contenedor ? c.texto2 : c.texto4 }}>
                      {datos.contenedor ?? 'sin cargar'}
                    </span>
                  )}
                </div>
              </div>

              {/* El completo no lleva conteo: total, descontado y saldo serían
                  tres ceros que no dicen nada. */}
              {/* En el celular, los tres números en una fila de tres: con el
                  `flex` de siempre el saldo, que es el que importa, caía solo
                  en un segundo renglón. */}
              {!entero && (
              <div
                className="grid grid-cols-3 gap-3 tablet:flex tablet:gap-6"
                style={{
                  padding: '16px 0',
                  borderTop: `1px solid ${c.borde}`,
                  borderBottom: `1px solid ${c.borde}`,
                  flexWrap: 'wrap',
                }}
              >
                {[
                  { r: 'Total original', v: datos.total, color: c.texto },
                  { r: 'Descontado', v: datos.usado, color: c.texto },
                  { r: 'Saldo remanente', v: datos.saldo, color: datos.saldo === 0 ? c.ambar : c.verde },
                ].map((m) => (
                  // El número abajo de todo: si un rótulo se parte en dos, los
                  // tres números siguen a la misma altura.
                  <div key={m.r} className="flex flex-col justify-between">
                    <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: c.texto3 }}>
                      {m.r}
                    </div>
                    {/* Cuenta hasta el valor y, al armar un capacho, se ve bajar el saldo. */}
                    <motion.div
                      initial={false}
                      animate={{ color: m.color }}
                      transition={suave}
                      style={{ fontSize: 27, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                    >
                      <Numero valor={m.v} formato={String} />
                    </motion.div>
                  </div>
                ))}
              </div>
              )}

              {/* Lo que ZF pidió de lo que hay arriba de este pallet.
                  Aceptarlo es decir de qué línea sale, y por eso se hace acá
                  y no en una pantalla aparte: la mercadería está a la vista. */}
              {/* Al aceptar el último, el bloque se pliega en vez de desaparecer. */}
              <AnimatePresence initial={false}>
              {datos.pedidos.length > 0 && (
                <motion.div key="pedidos" {...desplegar} style={{ ...desplegar.style, position: 'relative', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '.09em',
                      textTransform: 'uppercase',
                      color: c.texto3,
                    }}
                  >
                    {entero ? 'Pedidos de ZF que este pallet puede cumplir' : 'Pedidos de ZF esperando este pallet'}
                  </div>
                  {/* El aceptado se va y los demás suben a ocupar su lugar. */}
                  <AnimatePresence mode="popLayout">
                  {datos.pedidos.map((p, n) => {
                    const linea =
                      items.find((i) => i.articulo === p.articulo && i.saldo >= p.piezas) ??
                      (entero ? undefined : items.find((i) => i.articulo === p.articulo && i.saldo > 0));
                    return (
                      <motion.div
                        key={p.id}
                        layout
                        {...fila(n)}
                        transition={{ ...fila(n).transition, layout: resorte }}
                        // Grilla: código y fecha arriba, el detalle abajo con
                        // «Aceptar» a la derecha. Suelto se partía el detalle y el
                        // botón caía solo a un renglón. En la tablet la ficha va
                        // a lo ancho y entra en una fila; en el escritorio vuelve
                        // a ser media pantalla y vuelve la grilla.
                        className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1.5 tablet:flex tablet:gap-2.5 escritorio:grid escritorio:gap-x-3 escritorio:gap-y-1.5"
                        style={{
                          padding: '10px 14px',
                          borderRadius: 12,
                          border: `1px solid ${c.borde}`,
                          background: c.fondo,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: c.texto }}>{p.id}</span>
                        <span className="col-start-1 row-start-2" style={{ flex: 1, minWidth: 140, fontSize: 12.5, color: c.texto2 }}>
                          {p.articulo} · {entero ? 'pallet entero' : `${p.piezas} pz`}
                          {p.orden ? ` · ${p.orden}` : ''}
                        </span>
                        <span className="col-start-2 row-start-1 justify-self-end" style={{ fontSize: 12, color: c.texto4 }}>{p.pedidoEn}</span>
                        <button
                          type="button"
                          className="col-start-2 row-start-2 justify-self-end"
                          onClick={() => linea && aceptar(p.id, linea.id)}
                          disabled={!linea}
                          title={
                            linea
                              ? `Aceptar contra ${linea.articulo}`
                              : `No hay saldo de ${p.articulo} en este pallet para ${p.piezas} piezas`
                          }
                          style={{
                            ...botonPrimario,
                            minHeight: altoMinimo(34),
                            padding: '6px 14px',
                            fontSize: 13,
                            opacity: linea ? 1 : 0.45,
                            cursor: linea ? 'pointer' : 'default',
                          }}
                        >
                          Aceptar
                        </button>
                      </motion.div>
                    );
                  })}
                  </AnimatePresence>
                </motion.div>
              )}
              </AnimatePresence>

              {/* El contenido real del pallet. Con varios artículos, el saldo
                  que importa es el de la línea, no el del bulto. */}
              {items.length > 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '.09em',
                      textTransform: 'uppercase',
                      color: c.texto3,
                    }}
                  >
                    De qué artículo sale el capacho
                  </div>
                  {items.map((it) => {
                    const activa = it.id === elegida;
                    const agotada = it.saldo === 0;
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setLinea(it.id)}
                        disabled={agotada}
                        // En el celular, artículo y saldo arriba y la descripción
                        // abajo a lo ancho: al medio quedaba en una columna angosta.
                        className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-0.5 tablet:flex tablet:gap-3"
                        style={{
                          position: 'relative',
                          isolation: 'isolate',
                          padding: '10px 14px',
                          borderRadius: 12,
                          border: `1px solid ${c.borde}`,
                          background: c.panel,
                          cursor: agotada ? 'default' : 'pointer',
                          opacity: agotada ? 0.55 : 1,
                          textAlign: 'left',
                          width: '100%',
                        }}
                      >
                        {/* El marco verde de la elegida viaja de una línea a la otra. */}
                        {activa && (
                          <motion.span
                            layoutId={`linea-elegida-${pallet}`}
                            transition={resorte}
                            aria-hidden="true"
                            style={{
                              position: 'absolute',
                              inset: -1,
                              zIndex: -1,
                              borderRadius: 12,
                              border: `1px solid ${c.verde}`,
                              background: c.verdeSuave,
                            }}
                          />
                        )}
                        <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: c.texto }}>
                          {it.articulo}
                        </span>
                        <span className="col-span-2 row-start-2" style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: c.texto2 }}>
                          {it.descripcion}
                          {it.lote ? ` · ${it.lote}` : ''}
                        </span>
                        <span
                          className="col-start-2 row-start-1 justify-self-end"
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            color: agotada ? c.ambar : c.verde,
                          }}
                        >
                          {agotada ? 'sin saldo' : `saldo ${it.saldo}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {entero ? (
                <div style={{ fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
                  Este pallet sale completo: no se le arman capachos. Aceptá el pedido de ZF de acá arriba y, cuando
                  salga del depósito, marcalo entregado. Ahí libera la posición.
                </div>
              ) : (
              // En el celular las piezas a lo ancho con «cuántos» al lado, y el
              // botón abajo a lo ancho: suelto a la izquierda parecía otra cosa.
              <div className="grid grid-cols-[1fr_auto] items-end gap-2.5 tablet:flex tablet:flex-wrap">
                <label style={etiquetaCampo}>
                  {lineaActual && items.length > 1
                    ? `Piezas de ${lineaActual.articulo}`
                    : 'Piezas del próximo capacho'}
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={cantidad}
                    onChange={(e) => setCantidad(e.target.value)}
                    className="w-full tablet:w-[150px]"
                    style={{
                      height: 48,
                      padding: '0 16px',
                      border: `1px solid ${c.bordeFuerte}`,
                      borderRadius: 999,
                      background: c.panel,
                      fontSize: 16,
                      fontWeight: 600,
                      color: c.texto,
                    }}
                  />
                </label>
                {/* Para armar varios capachos iguales de una sola vez, sin
                    repetir el gesto de cargar las piezas cada vez. */}
                <label style={etiquetaCampo}>
                  Cuántos capachos
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={cuantos}
                    onChange={(e) => setCuantos(e.target.value)}
                    style={{
                      width: 90,
                      height: 48,
                      padding: '0 16px',
                      border: `1px solid ${c.bordeFuerte}`,
                      borderRadius: 999,
                      background: c.panel,
                      fontSize: 16,
                      fontWeight: 600,
                      color: c.texto,
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="col-span-2 justify-center"
                  onClick={agregar}
                  disabled={ocupado}
                  style={{ ...botonPrimario, minHeight: 48, padding: '0 20px', fontSize: 14, opacity: ocupado ? 0.7 : 1 }}
                >
                  {/* El rótulo sigue a «cuántos»: el viejo sale y el nuevo entra. */}
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={Number(cuantos) > 1 ? Math.trunc(Number(cuantos)) : 1}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6, transition: { duration: 0.12 } }}
                      transition={suave}
                    >
                      {Number(cuantos) > 1 ? `Agregar ${Math.trunc(Number(cuantos))} capachos` : 'Agregar capacho'}
                    </motion.span>
                  </AnimatePresence>
                </button>
              </div>
              )}

              <AnimatePresence>
                {avisoTexto && (
                  <motion.div key={avisoTexto} {...aparecer} role="alert" style={aviso('warn')}>
                    {avisoTexto}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden', position: 'relative' }}>
              {/* En el celular el título va solo arriba y los contadores abajo,
                  corriendo de costado si no entran: al lado del título se
                  salían de la tarjeta. */}
              <div
                className="flex-wrap"
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}
              >
                <span className="flex-1 whitespace-nowrap max-escritorio:basis-full" style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>
                  {entero ? 'Salida del pallet' : 'Capachos armados'}
                </span>
                {/* Cuántos hay en cada estado. Sólo los que tienen algo: una
                    fila de ceros no dice nada y ocupa el mismo lugar. */}
                {/* Un chip por estado, siempre el mismo: cambia su número en el
                    lugar, y el que aparece o se va empuja a los otros. */}
                <div className="wms-tira tablet:gap-3 max-escritorio:flex-1">
                  <AnimatePresence mode="popLayout" initial={false}>
                  {datos.capachos.length === 0 ? (
                    <motion.span key="cero" layout {...pop} style={{ display: 'inline-flex' }}>
                      <EstadoChip estado="0" tono="neu" />
                    </motion.span>
                  ) : (
                    ESTADOS_CAPACHO.filter((e) => datos.porEstado[e] > 0).map((e) => (
                      <motion.span key={e} layout {...pop} style={{ display: 'inline-flex' }}>
                        <EstadoChip estado={`${datos.porEstado[e]} ${e.toLowerCase()}`} tono={tonoDeEstado(e)} />
                      </motion.span>
                    ))
                  )}
                  </AnimatePresence>
                </div>
              </div>

              {/* El capacho que se arma entra y empuja; el que se quita sale y
                  los de abajo suben. La tarjeta es `relative` por el popLayout. */}
              <AnimatePresence mode="popLayout">
              {datos.capachos.map((cap, i) => {
                const paso = ESTADOS_CAPACHO.indexOf(cap.estado);
                const siguiente = ESTADOS_CAPACHO[paso + 1];
                const anterior = ESTADOS_CAPACHO[paso - 1];
                // 44 px: se tocan con guantes, en el celular.
                const redondo = {
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  border: `1px solid ${c.borde}`,
                  background: c.panel,
                  display: 'grid',
                  placeItems: 'center',
                  cursor: 'pointer',
                } as const;
                // En celular y tablet el renglón se arma como tarjeta: código,
                // artículo y estado; después las piezas, y abajo los botones a
                // lo ancho. En el escritorio, dos renglones: arriba lo mismo y
                // abajo las piezas con los botones a la derecha. En uno solo no
                // entraba en media pantalla: el artículo se partía, el chip se
                // le encimaba y los botones caían sueltos a otro renglón.
                return (
                <motion.div
                  key={cap.id}
                  layout
                  {...fila(i)}
                  transition={{ ...fila(i).transition, layout: resorte }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${c.borde}`, flexWrap: 'wrap', background: c.panel }}
                >
                  {/* Cuál entra en la próxima impresión. El pallet que sale
                      entero no lleva etiqueta de capacho: sale con la que trae,
                      así que no hay nada que tildar. */}
                  {cap.unidad !== 'pallet' && !cap.parteDe && <Tilde e={emision} id={cap.id} estado={cap.estado} />}
                  <span style={{ fontFamily: mono, fontSize: 13.5, fontWeight: 500, color: c.texto }}>{cap.id}</span>
                  {/* La parte va en la caja del otro capacho, con su etiqueta. */}
                  {/* En el celular y la tablet «completa …» baja al segundo
                      renglón (la copia de abajo): arriba no entraba con el estado. */}
                  {cap.parteDe && <span className="hidden escritorio:inline" style={{ fontSize: 12.5, color: c.texto3 }}>completa {cap.parteDe}</span>}
                  {/* De qué artículo es: en un pallet mezclado, dos capachos
                      del mismo pallet no llevan lo mismo adentro. */}
                  <span className="min-w-max" style={{ flex: 1, fontFamily: mono, fontSize: 12, color: c.texto3 }}>
                    {cap.articulo}
                  </span>
                  {/* El chip es siempre el mismo: al moverlo cambia de color y de
                      texto en el lugar (lo hace EstadoChip). Fuera del escritorio
                      cierra el primer renglón, a la derecha del código. */}
                  <span style={{ display: 'inline-flex' }}>
                    <EstadoChip estado={cap.estado} />
                  </span>
                  <span aria-hidden className="basis-full" />
                  {cap.parteDe && <span className="escritorio:hidden" style={{ fontSize: 12.5, color: c.texto3 }}>completa {cap.parteDe} ·</span>}
                  <AnimatePresence initial={false}>
                    {cap.etiquetado && (
                      <motion.span key="etiquetado" {...pop} style={{ display: 'inline-flex' }}>
                        <Icono nombre="label" size={17} color={c.verde} />
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {/* El pallet entero no lleva conteo de piezas. */}
                  {cap.unidad === 'pallet' ? (
                    <span style={{ fontSize: 12.5, color: c.texto3 }}>el pallet entero</span>
                  ) : (
                    <>
                      <span style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: c.texto }}>
                        {cap.piezas}
                      </span>
                      <span style={{ fontSize: 12.5, color: c.texto3 }}>piezas</span>
                    </>
                  )}

                  {/* Un paso para adelante y uno para atrás: saltear no se
                      puede, y el botón de más se corrige volviendo. Con
                      texto en vez de flechas: en la mesa, con guantes, una
                      flecha no dice a qué estado lleva. */}
                  <div className="flex basis-full items-center gap-2.5 escritorio:ml-auto escritorio:basis-auto">
                  <button
                    type="button"
                    className="max-escritorio:flex-1 justify-center"
                    onClick={() => anterior && mover(cap.id, cap.estado, -1)}
                    disabled={!anterior}
                    title={anterior ? `Volver a ${anterior}` : 'Es el primer estado'}
                    aria-label={anterior ? `Volver ${cap.id} a ${anterior}` : 'Sin estado anterior'}
                    style={{
                      ...botonSecundario,
                      minHeight: 44,
                      padding: '0 16px',
                      color: c.texto3,
                      opacity: anterior ? 1 : 0.35,
                      cursor: anterior ? 'pointer' : 'default',
                    }}
                  >
                    Volver
                  </button>
                  <button
                    type="button"
                    className="max-escritorio:flex-1 justify-center"
                    onClick={() => siguiente && mover(cap.id, cap.estado, 1)}
                    disabled={!siguiente}
                    title={siguiente ? `Marcar ${siguiente}` : 'Ya está entregado'}
                    aria-label={siguiente ? `Marcar ${cap.id} como ${siguiente}` : 'Ya está entregado'}
                    style={{
                      ...botonSecundario,
                      minHeight: 44,
                      padding: '0 16px',
                      border: `1px solid ${siguiente ? c.verde : c.borde}`,
                      background: siguiente ? c.verdeSuave : c.panel,
                      color: siguiente ? c.verde : c.texto4,
                      opacity: siguiente ? 1 : 0.35,
                      cursor: siguiente ? 'pointer' : 'default',
                    }}
                  >
                    {/* El color lo cambia la transición de los botones; el rótulo sale y entra. */}
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={siguiente ?? 'fin'}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6, transition: { duration: 0.12 } }}
                        transition={suave}
                      >
                        {siguiente === 'Aceptado' ? 'Aceptar' : siguiente === 'Entregado' ? 'Entregar' : 'Entregado'}
                      </motion.span>
                    </AnimatePresence>
                  </button>

                  {/* Lo entregado no se borra: devolvería saldo por mercadería
                      que ya salió del depósito. Primero se vuelve a Aceptado.
                      Va apartado de las flechas para no tocarlo por errarle a
                      «Marcar». */}
                  <AnimatePresence initial={false}>
                  {cap.estado !== 'Entregado' && !cap.parteDe && (
                    <motion.button
                      key="quitar"
                      {...pop}
                      type="button"
                      onClick={() =>
                        quitar(cap.id, cap.unidad === 'pallet' ? undefined : `Sus ${cap.piezas} piezas vuelven al saldo del pallet.`)
                      }
                      aria-label={`Quitar ${cap.id}`}
                      title="Quitar el capacho"
                      style={{ ...redondo, color: c.rojo, marginLeft: 16 }}
                    >
                      <Icono nombre="delete" size={19} />
                    </motion.button>
                  )}
                  </AnimatePresence>
                  </div>
                </motion.div>
                );
              })}
              </AnimatePresence>

              <AnimatePresence initial={false}>
              {datos.capachos.length === 0 && (
                <motion.div key="vacio" {...aparecer} style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, color: c.texto2 }}>
                  {entero
                    ? 'Este pallet todavía no está comprometido con ningún pedido de ZF.'
                    : 'Todavía no hay capachos. Agregá el primero con la cantidad de piezas.'}
                </motion.div>
              )}
              </AnimatePresence>

              {entero ? (
                <div style={{ padding: '14px 16px', fontSize: 12.5, color: c.texto3, textWrap: 'pretty' }}>
                  Este pallet no lleva etiqueta de capacho: sale como entró, con la que se le pegó al recibirlo.
                </div>
              ) : (
                <>
                {/* Aire entre el último capacho y el texto de la etiqueta, que si
                    no arranca pegado al borde de la fila. */}
                {(pedidos?.pedidas.length ?? 0) > 0 && <div aria-hidden className="h-3.5 escritorio:hidden" />}
                <Emision
                  e={emision}
                  pedidas={pedidos?.pedidas ?? []}
                  grupos={datos.grupos}
                  generar={(g, d, ids) => api.generarEtiquetas(pallet, g, d, ids)}
                  onAviso={setAvisoTexto}
                />
                </>
              )}
            </motion.div>
          </div>
        )
      )}

      {esAdmin && datos && items.length > 0 && <CorregirCantidad pallet={pallet} items={items} onListo={recargar} />}
      <HistorialDelPallet pallet={pallet} />
      {dialogo}
    </div>
  );
}

/* ---------------------------------------------------------------- etiquetas */

/**
 * Lo que comparten la ficha del pallet y la de la orden para emitir las
 * etiquetas de capacho: la plantilla, cuáles se imprimen y los datos que la
 * plantilla pide.
 *
 * Los datos van **por grupo** —pallet y producto—, porque cada producto trae
 * la factura y el documento de recepción de su contenedor. La clave `''` es el
 * formulario suelto de los capachos sin grupo, que son los anteriores a las
 * líneas de pallet.
 *
 * Lo precargado —lo que el sistema ya sabe— llena lo que está en blanco y no
 * pisa lo que el operario ya escribió: la ficha se refresca sola.
 */
function useEmision(
  capachos: { id: string; estado: EstadoCapacho; unidad: string; parteDe?: string | null }[],
  grupos?: GrupoDeEtiqueta[],
  sueltos?: Record<string, string>,
) {
  const [plantilla, setPlantilla] = useState<EtiquetaPlantilla | null>(null);
  const [etiquetas, setEtiquetas] = useState<Etiqueta[] | null>(null);
  /**
   * Cuáles se imprimen. Vienen marcados los que todavía no salieron: la
   * etiqueta de un capacho entregado no se pega en ningún lado, y tildarlos de
   * a uno cada vez es trabajo que la pantalla puede ahorrar.
   */
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const capachosVistos = useRef('');
  const [cargados, setCargados] = useState<DatosPorGrupo>({});

  // La etiqueta que diseñó el administrador. Se busca una sola vez: no cambia
  // mientras se está fraccionando.
  useEffect(() => {
    void api
      .etiquetas('capacho')
      .then((r) => setPlantilla(r.etiquetas.find((e) => e.predeterminada) ?? null))
      .catch(() => setPlantilla(null));
  }, []);

  const huellaPrecarga = JSON.stringify([grupos?.map((g) => [g.clave, g.cargados]) ?? [], sueltos ?? {}]);
  useEffect(() => {
    const [porGrupo, suelto] = JSON.parse(huellaPrecarga) as [[string, Record<string, string>][], Record<string, string>];
    setCargados((antes) => {
      const ahora: DatosPorGrupo = {};
      for (const [clave, nuevos] of [...porGrupo, ['', suelto] as [string, Record<string, string>]]) {
        const escritos = Object.entries(antes[clave] ?? {}).filter(([, v]) => v.trim());
        ahora[clave] = { ...nuevos, ...Object.fromEntries(escritos) };
      }
      return ahora;
    });
  }, [huellaPrecarga]);

  // Cuando aparece un capacho nuevo —o se entrega uno— se vuelve a marcar lo
  // que no salió. Mientras la lista sea la misma no se toca: lo que el operario
  // destildó tiene que quedar destildado.
  const huella = capachos.map((cap) => `${cap.id}:${cap.estado}`).join(',');
  useEffect(() => {
    if (huella === capachosVistos.current) return;
    capachosVistos.current = huella;
    // La salida de un pallet entero no entra: no lleva etiqueta de capacho, y
    // dejarla marcada haría que «Generar 1 etiqueta» pida una que no existe.
    // Lo pedido tampoco: sin pallet no hay etiqueta que armarle.
    setElegidos(
      new Set(
        capachos
          .filter((cap) => cap.estado === 'Aceptado' && cap.unidad !== 'pallet' && !cap.parteDe)
          .map((cap) => cap.id),
      ),
    );
  }, [huella, capachos]);

  const alternar = (id: string) => {
    // Lo ya generado es de la selección de antes: dejarlo sería imprimir lo
    // que se acaba de destildar.
    setEtiquetas(null);
    setElegidos((antes) => {
      const ahora = new Set(antes);
      if (!ahora.delete(id)) ahora.add(id);
      return ahora;
    });
  };

  return { plantilla, etiquetas, setEtiquetas, elegidos, alternar, cargados, setCargados };
}

type EstadoEmision = ReturnType<typeof useEmision>;

/** El tilde de «entra en la próxima impresión». */
function Tilde({ e, id, estado }: { e: EstadoEmision; id: string; estado: EstadoCapacho }) {
  // El label agranda lo que se toca a 44 px alrededor del tilde de 18; el
  // margen negativo devuelve lo que agranda, así la fila no se mueve.
  return (
    <label style={{ display: 'grid', placeItems: 'center', width: 44, minHeight: 44, margin: -13, cursor: 'pointer' }}>
    <input
      type="checkbox"
      checked={e.elegidos.has(id)}
      onChange={() => e.alternar(id)}
      aria-label={`Imprimir la etiqueta de ${id}`}
      title={
        estado === 'Entregado'
          ? 'Ya salió del depósito: su etiqueta no se vuelve a pegar, pero se puede reimprimir.'
          : 'Entra en la próxima impresión'
      }
      style={{ width: 18, height: 18, accentColor: c.verde, cursor: 'pointer' }}
    />
    </label>
  );
}

/** Una grilla de campos de la etiqueta. */
function Campos({
  pedidas,
  valor,
  poner,
}: {
  pedidas: PedidosDeEtiqueta['pedidas'];
  valor: (campo: string) => string;
  poner: (campo: string, v: string) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 10 }}>
      {pedidas.map((p) => {
        // La opcional en blanco no se pinta de ámbar: no falta nada.
        const vacio = !p.opcional && !valor(p.clave).trim();
        return (
          <label key={p.clave} style={etiquetaCampo}>
            {p.nombre}
            {/* Al completarlo, el ámbar se apaga de a poco. */}
            <motion.input
              value={valor(p.clave)}
              onChange={(ev) => poner(p.clave, ev.target.value)}
              initial={false}
              animate={{
                borderColor: vacio ? c.ambar : c.bordeFuerte,
                backgroundColor: vacio ? c.ambarSuave : c.panel,
              }}
              transition={suave}
              style={campo}
            />
          </label>
        );
      })}
    </div>
  );
}

/** Los datos a mano, el botón de «Imprimir N» (genera e imprime junto), y la vista previa. */
function Emision({
  e,
  pedidas,
  grupos,
  generar,
  onAviso,
}: {
  e: EstadoEmision;
  pedidas: PedidosDeEtiqueta['pedidas'];
  grupos: GrupoDeEtiqueta[];
  generar: (grupos: DatosPorGrupo, sueltos: Record<string, string>, capachos: string[]) => Promise<{ etiquetas: Etiqueta[] }>;
  onAviso: (texto: string) => void;
}) {
  const { plantilla, etiquetas, elegidos, cargados, setCargados } = e;

  // Sólo se piden los datos de lo que se va a imprimir. Sin grupos (capachos
  // viejos, sin línea de pallet) queda el formulario suelto de siempre.
  const activos = grupos.filter((g) => g.capachos.some((id) => elegidos.has(id)));
  const claves = activos.length > 0 ? activos.map((g) => g.clave) : [''];
  const comunes = pedidas.filter((p) => !p.deIngreso);
  const deIngreso = pedidas.filter((p) => p.deIngreso);
  const valor = (clave: string, campo: string) => cargados[clave]?.[campo] ?? '';
  const poner = (en: string[], campo: string, v: string) =>
    setCargados((antes) => {
      const ahora = { ...antes };
      for (const clave of en) ahora[clave] = { ...ahora[clave], [campo]: v };
      return ahora;
    });
  const faltan = pedidas.filter((p) => !p.opcional && claves.some((k) => !valor(k, p.clave).trim()));

  /**
   * «Imprimir N» hace las dos cosas de un solo gesto. Lo que antes eran dos
   * clics —generar, después imprimir— quedaba raro con un solo capacho: el
   * operario no entendía por qué hacía falta tocar dos veces para una sola
   * etiqueta. Lo que no se puede apurar es leer el HTML ya dibujado, que sólo
   * existe después del re-render con las etiquetas generadas: por eso la
   * impresión queda pendiente en el ref y sale recién en el `useEffect`.
   */
  const imprimirPendiente = useRef(false);

  async function emitirEImprimir() {
    try {
      const res = await generar(
        Object.fromEntries(claves.filter(Boolean).map((k) => [k, cargados[k] ?? {}])),
        cargados[''] ?? {},
        [...elegidos],
      );
      imprimirPendiente.current = true;
      e.setEtiquetas(res.etiquetas);
      onAviso('');
    } catch (err) {
      e.setEtiquetas(null);
      onAviso(mensajeDeError(err));
    }
  }

  useEffect(() => {
    if (!imprimirPendiente.current) return;
    imprimirPendiente.current = false;
    if (!plantilla || !etiquetas || etiquetas.length === 0) return;
    const dibujos = etiquetas.map((x) => document.getElementById(`wms-etq-${x.id}`)?.innerHTML ?? '');
    const problema = imprimirEtiquetas(plantilla, dibujos.filter(Boolean));
    if (problema) onAviso(problema);
  }, [etiquetas, plantilla]);

  return (
    <>
      {pedidas.length > 0 && (
        <div style={{ padding: '0 16px 4px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
            La etiqueta lleva estos datos. Los del ingreso vienen del contenedor de cada producto; sin completarlos no
            se emite.
          </div>
          {/* Lo que no depende del ingreso (el remito, por ejemplo) se carga una
              vez y vale para todas. Con un solo grupo va todo junto, como
              siempre. */}
          {(claves.length === 1 ? pedidas : comunes).length > 0 && (
            <Campos
              pedidas={claves.length === 1 ? pedidas : comunes}
              valor={(campo) => valor(claves[0]!, campo)}
              poner={(campo, v) => poner(claves, campo, v)}
            />
          )}
          {/* Al tildar un capacho de otro producto, su grupo de datos se abre en el lugar. */}
          <AnimatePresence initial={false}>
          {claves.length > 1 &&
            deIngreso.length > 0 &&
            activos.map((g) => (
              <motion.div
                key={g.clave}
                {...abrir(14)}
                style={{ border: `1px solid ${c.borde}`, borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                <div style={{ fontSize: 13, color: c.texto2 }}>
                  <strong style={{ fontFamily: mono, color: c.texto }}>{g.pallet}</strong> · {g.articulo}
                  {g.descripcion && g.descripcion !== g.articulo ? ` · ${g.descripcion}` : ''}
                  <span style={{ color: c.texto3 }}>
                    {' '}
                    · {g.contenedor ? `contenedor ${g.contenedor}` : 'sin contenedor'} ·{' '}
                    {g.capachos.filter((id) => elegidos.has(id)).length} etiquetas
                  </span>
                </div>
                <Campos pedidas={deIngreso} valor={(campo) => valor(g.clave, campo)} poner={(campo, v) => poner([g.clave], campo, v)} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <div style={{ padding: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* A lo ancho en el celular: es el botón que cierra la mesa y va con el pulgar. */}
        <button
          type="button"
          className="max-tablet:w-full justify-center"
          onClick={emitirEImprimir}
          disabled={faltan.length > 0 || elegidos.size === 0}
          title={
            faltan.length
              ? `Falta cargar: ${faltan.map((f) => f.nombre).join(', ')}`
              : elegidos.size === 0
                ? 'Marcá al menos un capacho'
                : undefined
          }
          style={{
            ...botonPrimario,
            minHeight: 44,
            padding: '0 18px',
            opacity: faltan.length || elegidos.size === 0 ? 0.55 : 1,
            cursor: faltan.length || elegidos.size === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <Icono nombre="print" size={19} />
          {/* En un solo span: el botón separa sus hijos con `gap`. */}
          <span>
            Imprimir <Numero valor={elegidos.size} formato={String} />
          </span>
        </button>

        <AnimatePresence initial={false}>
        {faltan.length > 0 && (
          <motion.span key="faltan" {...aparecer} style={{ fontSize: 12.5, color: c.ambar }}>
            Falta {faltan.map((f) => f.nombre.toLowerCase()).join(', ')}.
          </motion.span>
        )}
        </AnimatePresence>
      </div>

      {/* La vista previa se abre desde el centro y cada etiqueta entra detrás
          de la anterior. El dibujo de adentro no se anima: es lo que se imprime. */}
      <AnimatePresence>
      {etiquetas && etiquetas.length > 0 && (
        <motion.div key="vista-previa" {...abrir(16)} style={{ padding: '0 16px 18px' }}>
          {!plantilla && (
            <div style={{ ...aviso('warn'), marginBottom: 12 }}>
              Todavía no hay una etiqueta elegida para imprimir. Un administrador la diseña en Etiquetas y la marca
              con «Usar esta».
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {etiquetas.map((x, i) =>
              plantilla ? (
                <motion.div
                  key={x.id}
                  {...pop}
                  transition={{ ...pop.transition, delay: Math.min(i, 12) * 0.035 }}
                  id={`wms-etq-${x.id}`}
                  title={x.texto}
                  style={{
                    border: `1px solid ${c.borde}`,
                    borderRadius: 8,
                    background: '#fff',
                    overflow: 'hidden',
                    lineHeight: 0,
                  }}
                >
                  <EtiquetaDibujada
                    plantilla={plantilla.plantilla}
                    ancho={plantilla.ancho}
                    alto={plantilla.alto}
                    orientacion={plantilla.orientacion}
                    datos={x.datos}
                    style={{ width: 'auto', height: 150 }}
                  />
                </motion.div>
              ) : (
                <motion.span
                  key={x.id}
                  {...pop}
                  transition={{ ...pop.transition, delay: Math.min(i, 12) * 0.035 }}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 12,
                    border: `1px dashed ${c.verde}`,
                    background: c.verdeSuave,
                    fontFamily: mono,
                    fontSize: 12.5,
                    color: c.texto,
                  }}
                >
                  {x.texto}
                </motion.span>
              ),
            )}
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </>
  );
}

/* ------------------------------------------------------------------ órdenes */

/** Cuánto de la orden falta y cuánto ya salió, en una línea. */
function AvanceDeOrden({ orden }: { orden: OrdenEnMesa }) {
  return (
    // El chip que aparece o se va empuja a los otros. El de «N de M» es el
    // mismo dibujo que EstadoChip, armado acá para que los números cuenten.
    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <AnimatePresence mode="popLayout" initial={false}>
        {orden.pedidos > 0 && (
          <motion.span key="pedidos" layout {...pop} style={{ display: 'inline-flex' }}>
            <EstadoChip estado={`${orden.pedidos} sin aceptar`} tono="warn" />
          </motion.span>
        )}
        {orden.aceptados > 0 && (
          <motion.span key="aceptados" layout {...pop} style={{ display: 'inline-flex' }}>
            <EstadoChip estado={`${orden.aceptados} aceptados`} tono="ok" />
          </motion.span>
        )}
        <motion.span key="entregados" layout transition={resorte} style={chip('neu')}>
          <span style={punto('neu')} />
          <span>
            <Numero valor={orden.entregados} formato={String} /> de <Numero valor={orden.salidas} formato={String} />{' '}
            entregados
          </span>
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/**
 * Una orden de trabajo entera: cada salida con lo que le falta, y todo lo que
 * hace falta para completarla sin ir pallet por pallet —aceptar contra el
 * pallet que conviene, etiquetar los capachos y entregar—.
 */
function DetalleOrden({
  orden,
  onAbrir,
  onVolver,
  onIr,
}: {
  orden: string;
  onAbrir: (id: string) => void;
  onVolver: () => void;
  onIr: (v: Vista, id?: string) => void;
}) {
  const { vistas } = useSesion();
  const [avisoTexto, setAvisoTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [escaneado, setEscaneado] = useState('');
  const [leido, setLeido] = useState('');
  const [camara, setCamara] = useState(false);
  const { datos, cargando, error, recargar } = useApi(() => api.ordenEnMesa(orden), [orden], VIVO.rapido);
  const salidas = datos?.salidas ?? [];
  const emision = useEmision(salidas, datos?.grupos, datos?.cargados);
  const [confirmar, dialogo] = useConfirmar();
  const [agregando, setAgregando] = useState(false);

  async function mover(id: string, estado: EstadoCapacho, item?: number) {
    try {
      await api.moverCapacho(id, estado, item);
      setAvisoTexto('');
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    } finally {
      recargar();
    }
  }

  /** Lo saca de la orden y lo deja armado para otra; la orden vuelve a pedir esas piezas. */
  async function sacar(id: string) {
    const ok = await confirmar({
      titulo: `¿Sacar ${id} de ${orden}?`,
      detalle: 'Queda armado, con su pallet y su etiqueta, para usarlo en otra orden. Esta orden vuelve a pedir esas piezas.',
      boton: 'Sacar de la orden',
    });
    if (!ok) return;
    setLeido('');
    try {
      const r = await api.sacarCapacho(id);
      setAvisoTexto('');
      setLeido(`${id} quedó armado sin orden. La orden lo vuelve a pedir como ${r.pedido}.`);
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    } finally {
      recargar();
    }
  }

  /** El pedido toma un capacho que quedó armado de otra orden. */
  async function usarArmado(id: string, armado: string) {
    setLeido('');
    try {
      await api.usarArmado(id, armado);
      setAvisoTexto('');
      setLeido(`${armado} pasa a esta orden en lugar de ${id} y queda entregado.`);
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    } finally {
      recargar();
    }
  }

  /**
   * Acepta contra `pallet` todo lo que él puede cubrir: es lo que hace el
   * escaneo en la estación. No hay «aceptar todo» contra el pallet
   * recomendado: se acepta escaneando el pallet que de verdad está en la mesa.
   *
   * Se vuelve a pedir la orden entre una y otra: aceptar baja el saldo de esa
   * línea, y lo que le alcanzaba a la anterior puede no alcanzarle a la
   * siguiente. Al primer error se frena: seguir sería aceptar a ciegas.
   */
  async function aceptarContra(pallet: string) {
    setOcupado(true);
    const probadas = new Set<string>();
    try {
      let aceptadas = 0;
      for (;;) {
        const ahora = await api.ordenEnMesa(orden);
        const elegir = (x: SalidaDeOrden) => x.candidatos.find((k) => k.pallet === pallet);
        const s = ahora.salidas.find((x) => x.estado === 'Pedido' && elegir(x) && !probadas.has(x.id));
        if (!s) break;
        probadas.add(s.id);
        await api.moverCapacho(s.id, 'Aceptado', elegir(s)!.item);
        aceptadas++;
      }
      setAvisoTexto(
        aceptadas === 0
          ? `${pallet} no cubre ninguna salida pendiente de esta orden: no es del artículo, no le alcanza el saldo o ya está tomado.`
          : '',
      );
      if (aceptadas > 0) setLeido(`${pallet}: ${aceptadas} ${aceptadas === 1 ? 'salida aceptada' : 'salidas aceptadas'}.`);
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    } finally {
      setOcupado(false);
      recargar();
    }
  }

  /**
   * Lo que tira la pistola: la etiqueta de un capacho de la orden lo marca
   * entregado; el pallet, o la etiqueta `E-…` de la estación (que vale por el
   * pallet que tiene arriba), acepta contra él lo que pueda cubrir.
   */
  async function leer(bruto: string) {
    const codigo = bruto.trim().toUpperCase();
    if (!codigo) return;
    setLeido('');
    const salida = salidas.find((s) => s.id.toUpperCase() === codigo && !s.parteDe);
    if (salida) {
      if (salida.estado === 'Entregado') return setAvisoTexto(`${salida.id} ya está entregado.`);
      if (salida.estado === 'Pedido') return setAvisoTexto(`${salida.id} todavía no se aceptó contra un pallet.`);
      setOcupado(true);
      try {
        await api.moverCapacho(salida.id, 'Entregado');
        setAvisoTexto('');
        setLeido(`${salida.id} entregado.`);
      } catch (err) {
        setAvisoTexto(mensajeDeError(err));
      } finally {
        setOcupado(false);
        recargar();
      }
      return;
    }
    if (codigo.startsWith('CAP-')) {
      // Un capacho que quedó armado de otra orden: lo toma el pedido en que entra.
      const destino = salidas.find((s) => s.estado === 'Pedido' && s.armados.some((a) => a.id.toUpperCase() === codigo));
      if (!destino) return setAvisoTexto(`${codigo} no es de esta orden ni un capacho armado que le sirva.`);
      setOcupado(true);
      await usarArmado(destino.id, codigo);
      setOcupado(false);
      return;
    }
    if (!codigo.startsWith('E-')) return aceptarContra(codigo);
    try {
      const h = await api.buscar(codigo);
      if (!h.ocupante) return setAvisoTexto(`La estación ${codigo} no tiene un pallet arriba.`);
      await aceptarContra(h.ocupante.pallet);
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    }
  }

  /** Si se corta a mitad, avisa cuántas salidas entregó y cuál falló. */
  async function entregarTodo() {
    // Las partes se entregan con su capacho: contarlas aparte lo entregaría dos veces.
    const aceptadas = salidas.filter((s) => s.estado === 'Aceptado' && !s.parteDe);
    const ok = await confirmar({
      titulo: `¿Marcar como entregadas ${aceptadas.length} salidas de ${orden}?`,
      detalle: 'Su stock pasa de RDM a ZF.',
      boton: `Entregar ${aceptadas.length} ${aceptadas.length === 1 ? 'salida' : 'salidas'}`,
    });
    if (!ok) return;
    setOcupado(true);
    try {
      setAvisoTexto(await entregarCapachos(aceptadas.map((s) => s.id)));
    } finally {
      setOcupado(false);
      recargar();
    }
  }

  /**
   * Cierra la orden con lo que hay: lo pedido se borra y queda anotado como
   * faltante, lo aceptado se entrega, y los capachos que se marquen quedan
   * armados para otra orden.
   */
  async function cerrarConLoQueHay() {
    const aceptadas = salidas.filter((s) => s.estado === 'Aceptado' && !s.parteDe);
    const guardar = new Set<string>();
    const ok = await confirmar({
      titulo: `¿Cerrar ${orden} con lo que hay?`,
      detalle: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span>
            Lo que sigue pedido sale de la orden y queda el aviso de faltante para ZF.
            {aceptadas.length > 0 && ' Lo aceptado se entrega, salvo lo que marques para dejar armado.'}
          </span>
          {aceptadas.some((s) => s.unidad === 'capacho') && (
            <ElegirArmados salidas={aceptadas.filter((s) => s.unidad === 'capacho')} elegidos={guardar} />
          )}
        </div>
      ),
      boton: 'Cerrar la orden',
    });
    if (!ok) return;
    setOcupado(true);
    try {
      const r = await api.cerrarOrden(orden, [...guardar]);
      const texto = await entregarCapachos(aceptadas.filter((s) => !guardar.has(s.id)).map((s) => s.id));
      setAvisoTexto(texto);
      if (!texto && r.guardados.length > 0)
        setLeido(
          `${r.guardados.join(', ')} ${r.guardados.length === 1 ? 'quedó armado' : 'quedaron armados'} para otra orden.`,
        );
    } catch (err) {
      setAvisoTexto(mensajeDeError(err));
    } finally {
      setOcupado(false);
      recargar();
    }
  }

  // Agrupadas por artículo: es lo que el operario va a buscar.
  const porArticulo = new Map<string, SalidaDeOrden[]>();
  for (const s of salidas) porArticulo.set(s.articulo, [...(porArticulo.get(s.articulo) ?? []), s]);

  const capachos = salidas.filter((s) => s.unidad !== 'pallet');
  const conDeDonde = (s: SalidaDeOrden) => s.candidatos.length > 0 || s.armados.length > 0;
  const pendientes = salidas.filter((s) => s.estado === 'Pedido' && conDeDonde(s)).length;
  const sinPallet = salidas.filter((s) => s.estado === 'Pedido' && !conDeDonde(s)).length;
  const o = datos?.orden;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={onVolver} style={{ ...chipBoton(false), padding: '7px 14px' }}>
          Órdenes de trabajo
        </button>
        <Icono nombre="chevron_right" size={18} color={c.texto4} />
        {/* La miga de dónde estás se abre desde el centro, como el resto de lo que llega. */}
        <motion.span
          {...abrir(999)}
          style={{
            padding: '7px 14px',
            borderRadius: 999,
            background: c.verdeSuave,
            border: `1px solid ${c.verde}`,
            color: c.verde,
            fontFamily: mono,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {orden}
        </motion.span>
      </div>

      {error ? (
        <PanelError mensaje={error} onReintentar={recargar} />
      ) : cargando && !datos ? (
        <Cargando texto="Abriendo la orden…" />
      ) : (
        o && (
          <>
            <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: mono, fontSize: 18, fontWeight: 600, color: c.texto }}>{o.id}</span>
                <EstadoChip estado={o.estado} />
                <span style={{ flex: 1 }} />
                <AvanceDeOrden orden={o} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13.5, color: c.texto2 }}>
                {o.lineas.map((l) => (
                  <span key={l}>{l}</span>
                ))}
              </div>
              <div style={{ fontSize: 12.5, color: c.texto3 }}>
                Pedida {o.pedidaPor ? `por ${o.pedidaPor} ` : ''}el {o.pedidaEn}
                {o.entregaEstimada ? ` · entrega estimada ${fechaImpresa(o.entregaEstimada)}` : ''}
              </div>
              {o.observaciones && (
                <div style={{ fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>{o.observaciones}</div>
              )}

              {o.faltante && (
                <div role="status" style={{ ...aviso('warn'), display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <Icono nombre="production_quantity_limits" size={19} color={c.ambar} />
                  <span>Se cerró con faltante: {o.faltante}.</span>
                </div>
              )}

              {/* Lo que no se puede cumplir va arriba y en rojo: es lo que hay
                  que avisarle a ZF antes de que espere algo que no va a salir. */}
              <AnimatePresence initial={false}>
              {(o.faltantes.length > 0 || sinPallet > 0) && (
                <motion.div key="faltantes" {...abrir(16)} role="alert" style={{ ...aviso('err'), display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <Icono nombre="warning" size={19} color={c.rojo} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <strong>No hay stock para completar esta orden.</strong>
                    {o.faltantes.map((f) => (
                      <span key={f}>{f}.</span>
                    ))}
                    {sinPallet > 0 && (
                      <span>
                        {sinPallet} {sinPallet === 1 ? 'salida no tiene' : 'salidas no tienen'} un pallet que la
                        cubra. Avisale a ZF o esperá el próximo contenedor.
                      </span>
                    )}
                  </div>
                </motion.div>
              )}
              </AnimatePresence>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <AnimatePresence initial={false}>
                {o.aceptados > 0 && (
                  <motion.button
                    key="entregar-todo"
                    {...pop}
                    type="button"
                    onClick={entregarTodo}
                    disabled={ocupado}
                    style={{
                      ...botonSecundario,
                      minHeight: altoMinimo(42),
                      padding: '0 18px',
                      borderColor: c.verdeBorde,
                      color: c.verde,
                      opacity: ocupado ? 0.6 : 1,
                    }}
                  >
                    <Icono nombre="local_shipping" size={17} color={c.verde} />
                    Entregar todo lo aceptado
                  </motion.button>
                )}
                {o.estado !== 'Cancelada' && (
                  <motion.button
                    key="agregar"
                    {...pop}
                    type="button"
                    onClick={() => setAgregando((a) => !a)}
                    aria-expanded={agregando}
                    style={{ ...botonSecundario, minHeight: altoMinimo(42), padding: '0 18px' }}
                  >
                    <Icono nombre="playlist_add" size={17} color={c.texto3} />
                    Agregar a la orden
                  </motion.button>
                )}
                {/* Los remitos de la orden —puede viajar en partes— y, si salió
                    algo que todavía no viajó, armar el remito de eso. */}
                {vistas.includes('remitos') &&
                  o.remitos.map((r) => (
                    <motion.button
                      key={`remito-${r}`}
                      {...pop}
                      type="button"
                      onClick={() => onIr('remitos', r)}
                      style={{ ...botonSecundario, minHeight: altoMinimo(42), padding: '0 18px' }}
                    >
                      <Icono nombre="receipt_long" size={17} color={c.texto3} />
                      Ver remito {numeroImpreso(r)}
                    </motion.button>
                  ))}
                {vistas.includes('remitos') && o.porRemitir > 0 && (
                  <motion.button
                    key="hacer-remito"
                    {...pop}
                    type="button"
                    onClick={() => onIr('remitos', o.id)}
                    style={{ ...botonSecundario, minHeight: altoMinimo(42), padding: '0 18px' }}
                  >
                    <Icono nombre="note_add" size={17} color={c.texto3} />
                    {o.remitos.length > 0
                      ? `Remitir lo que falta (${o.porRemitir} ${o.porRemitir === 1 ? 'salida' : 'salidas'})`
                      : 'Hacer el remito'}
                  </motion.button>
                )}
                {/* Con algo ya aceptado o entregado y algo sin entregar: que no
                    quede para siempre en preparación. Sin nada empezado, se cancela. */}
                {o.pedidos + o.aceptados > 0 && o.aceptados + o.entregados > 0 && (
                  <motion.button
                    key="entregar-faltante"
                    {...pop}
                    type="button"
                    onClick={cerrarConLoQueHay}
                    disabled={ocupado}
                    style={{
                      ...botonSecundario,
                      minHeight: altoMinimo(42),
                      padding: '0 18px',
                      borderColor: c.ambar,
                      color: c.ambar,
                      opacity: ocupado ? 0.6 : 1,
                    }}
                  >
                    <Icono nombre="production_quantity_limits" size={17} color={c.ambar} />
                    Cerrar la orden
                  </motion.button>
                )}
                </AnimatePresence>
              </div>

              <AnimatePresence initial={false}>
                {agregando && (
                  <motion.div key="agregar" {...desplegar}>
                    <AgregarAOrden
                      orden={o.id}
                      unidad={o.unidad}
                      onListo={(texto) => {
                        setAgregando(false);
                        setAvisoTexto('');
                        setLeido(texto);
                        recargar();
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* En la estación no se elige la posición a mano: se escanea el
                  pallet que está arriba y se acepta contra él lo que cubra. */}
              <AnimatePresence initial={false}>
              {(pendientes > 0 || o.aceptados > 0) && (
                <motion.form
                  key="escaneo"
                  {...abrir(999)}
                  // Quieto, sin recorte: el campo toca el borde del form y el
                  // recorte le comería el anillo de foco.
                  animate={{ ...abrir(999).animate, transitionEnd: { clipPath: 'none' } }}
                  onSubmit={(ev) => {
                    ev.preventDefault();
                    void leer(escaneado);
                    setEscaneado('');
                  }}
                  style={{ display: 'flex', gap: 8, maxWidth: 520 }}
                >
                  <input
                    value={escaneado}
                    onChange={(ev) => setEscaneado(ev.target.value)}
                    disabled={ocupado}
                    placeholder="Escaneá el pallet, la estación (E-…) o el capacho (CAP-…)"
                    aria-label="Pallet o capacho escaneado"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    enterKeyHint="go"
                    style={{ ...campo, flex: 1, minWidth: 0, fontFamily: mono }}
                  />
                  <button
                    type="button"
                    onClick={() => setCamara(true)}
                    disabled={ocupado}
                    aria-label="Escanear con la cámara"
                    style={{ ...botonSecundario, justifyContent: 'center' }}
                  >
                    <Icono nombre="photo_camera" size={20} />
                  </button>
                </motion.form>
              )}
              </AnimatePresence>
              {camara && (
                <Escaner
                  onCerrar={() => setCamara(false)}
                  onLeer={(codigo) => {
                    setCamara(false);
                    void leer(codigo);
                  }}
                />
              )}

              <AnimatePresence>
                {leido && (
                  <motion.div key={`ok-${leido}`} {...aparecer} role="status" style={aviso('ok')}>
                    {leido}
                  </motion.div>
                )}
                {avisoTexto && (
                  <motion.div key={`warn-${avisoTexto}`} {...aparecer} role="alert" style={aviso('warn')}>
                    {avisoTexto}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <motion.div {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden' }}>
              {salidas.length === 0 && (
                <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, color: c.texto2 }}>
                  Esta orden no tiene salidas.
                </div>
              )}
              {/* Las filas cambian de alto al aceptar o entregar (se van los
                  candidatos, aparecen los botones): con `layout` las de abajo
                  se deslizan en vez de saltar. */}
              {[...porArticulo].map(([articulo, dela]) => (
                <div key={articulo}>
                  <motion.div
                    layout="position"
                    transition={resorte}
                    style={{
                      padding: '10px 16px',
                      background: c.fondo,
                      borderBottom: `1px solid ${c.borde}`,
                      fontSize: 13,
                      color: c.texto2,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ flex: 1, minWidth: 160 }}>
                        <strong style={{ fontFamily: mono, color: c.texto }}>{articulo}</strong>
                        {dela[0]!.descripcion && dela[0]!.descripcion !== articulo ? ` · ${dela[0]!.descripcion}` : ''}
                      </span>
                      {o.estado !== 'Cancelada' && (
                        <CantidadDeArticulo
                          orden={o.id}
                          articulo={articulo}
                          porPallet={o.unidad === 'pallet'}
                          // Lo que pide hoy: todas sus cajas con sus partes, o sus pallets.
                          actual={
                            o.unidad === 'pallet'
                              ? dela.filter((s) => !s.parteDe).length
                              : dela.reduce((n, s) => n + s.piezas, 0)
                          }
                          onListo={(texto) => {
                            setAvisoTexto('');
                            setLeido(texto);
                            recargar();
                          }}
                        />
                      )}
                    </span>
                  </motion.div>
                  {dela.map((s, i) => (
                    <FilaDeSalida
                      key={s.id}
                      i={i}
                      s={s}
                      e={emision}
                      ocupado={ocupado}
                      onAbrir={onAbrir}
                      onMover={mover}
                      onUsarArmado={usarArmado}
                      onSacar={sacar}
                    />
                  ))}
                </div>
              ))}

              {capachos.length > 0 && (
                <motion.div layout="position" transition={resorte} style={{ borderTop: `1px solid ${c.borde}`, paddingTop: 12 }}>
                  <div style={{ padding: '0 16px 10px', fontSize: 16, fontWeight: 600, color: c.texto }}>
                    Etiquetas de los capachos
                  </div>
                  <Emision
                    e={emision}
                    pedidas={datos.pedidas}
                    grupos={datos.grupos}
                    generar={(g, d, ids) => api.etiquetasDeOrden(orden, g, d, ids)}
                    onAviso={setAvisoTexto}
                  />
                </motion.div>
              )}
            </motion.div>
          </>
        )
      )}
      {dialogo}
    </div>
  );
}

/** «11:20» si fue hoy, «01/10» si fue otro día: lo que importa es si hace falta el día. */
function horaOFecha(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('sv-SE', { timeZone: HUSO }) === hoyISO()
    ? d.toLocaleTimeString('es-AR', { timeZone: HUSO, hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('es-AR', { timeZone: HUSO, day: '2-digit', month: '2-digit' });
}

/**
 * Cambiar cuánto pide la orden de un artículo. Lo aceptado o entregado no se
 * toca: se rehace lo que sigue pedido, y el servidor dice si no alcanza.
 */
function CantidadDeArticulo({
  orden,
  articulo,
  porPallet,
  actual,
  onListo,
}: {
  orden: string;
  articulo: string;
  porPallet: boolean;
  actual: number;
  onListo: (texto: string) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const n = Math.trunc(Number(valor));
  const valido = valor.trim() !== '' && Number.isFinite(n) && n >= 0 && n !== actual;

  async function guardar() {
    setEnviando(true);
    setError('');
    try {
      const r = await api.cambiarCantidad(orden, articulo, n);
      setEditando(false);
      onListo(`${r.detalle}.`);
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setEnviando(false);
    }
  }

  if (!editando)
    return (
      <button
        type="button"
        onClick={() => {
          setValor(String(actual));
          setError('');
          setEditando(true);
        }}
        style={{ ...botonSecundario, padding: '5px 12px', fontSize: 12.5 }}
      >
        <Icono nombre="edit" size={15} color={c.texto3} />
        {actual} {porPallet ? 'pallets' : 'pz'} · cambiar
      </button>
    );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valido) void guardar();
      }}
      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
    >
      <input
        type="number"
        inputMode="numeric"
        min={0}
        autoFocus
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        aria-label={porPallet ? `Pallets de ${articulo}` : `Piezas totales de ${articulo}`}
        style={{ ...campo, width: 110, minHeight: 36 }}
      />
      <span style={{ fontSize: 12.5 }}>{porPallet ? 'pallets' : 'pz'}</span>
      <button type="submit" disabled={!valido || enviando} style={{ ...botonPrimario, padding: '6px 14px', minHeight: 36, opacity: !valido || enviando ? 0.6 : 1 }}>
        {enviando ? 'Guardando…' : n === 0 && valor.trim() !== '' ? 'Quitar' : 'Guardar'}
      </button>
      <button type="button" onClick={() => setEditando(false)} style={{ ...botonSecundario, padding: '6px 12px', minHeight: 36 }}>
        Cancelar
      </button>
      {error && <span style={{ flexBasis: '100%', color: c.rojo, fontSize: 12.5 }}>{error}</span>}
    </form>
  );
}

/**
 * Agregarle a la orden un artículo, o más piezas o pallets de uno que ya
 * tiene, como en la solicitud: en capachos se escribe el total y se muestra en
 * cuántos sale; en pallets completos, cuántos pallets.
 */
function AgregarAOrden({ orden, unidad, onListo }: { orden: string; unidad: Unidad; onListo: (texto: string) => void }) {
  const catalogo = useApi(() => api.articulos(), []);
  const [articulo, setArticulo] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const porPallet = unidad === 'pallet';
  const disponibles = (catalogo.datos ?? []).filter((a) => a.tipo === (porPallet ? 'completo' : 'fraccionamiento'));
  const elegido = disponibles.find((a) => a.codigo === articulo);
  const n = Math.trunc(Number(cantidad)) || 0;
  const partido = !porPallet && elegido?.maxPorCapacho && n > 0 ? corte(n, elegido.maxPorCapacho) : null;

  async function agregar() {
    setEnviando(true);
    setError('');
    try {
      const r = await api.agregarAOrden(orden, [porPallet ? { articulo, capachos: n } : { articulo, piezas: n }]);
      onListo(`Se agregó a la orden: ${r.detalle}.`);
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, border: `1px solid ${c.borde}`, borderRadius: 16 }}>
      {catalogo.error && <PanelError mensaje={catalogo.error} onReintentar={catalogo.recargar} />}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ ...etiquetaCampo, flex: '2 1 240px' }}>
          Artículo
          <select value={articulo} onChange={(e) => setArticulo(e.target.value)} style={{ ...campo, padding: '0 12px' }}>
            <option value="">{catalogo.cargando && !catalogo.datos ? 'Buscando artículos…' : 'Elegí un artículo…'}</option>
            {disponibles.map((a) => (
              <option key={a.codigo} value={a.codigo}>
                {a.etiqueta}
              </option>
            ))}
          </select>
        </label>
        <label style={{ ...etiquetaCampo, flex: '1 1 120px' }}>
          {porPallet ? 'Pallets' : 'Piezas totales'}
          <input type="number" inputMode="numeric" min={1} value={cantidad} onChange={(e) => setCantidad(e.target.value)} style={campo} />
        </label>
        <button
          type="button"
          onClick={agregar}
          disabled={enviando || !articulo || n < 1}
          style={{ ...botonPrimario, opacity: enviando || !articulo || n < 1 ? 0.6 : 1 }}
        >
          {enviando ? 'Agregando…' : 'Agregar'}
        </button>
      </div>
      {elegido && (
        <span style={{ fontSize: 12, color: c.texto3 }}>
          {porPallet
            ? `${elegido.pallets} ${elegido.pallets === 1 ? 'pallet entero disponible' : 'pallets enteros disponibles'}`
            : `${elegido.piezas.toLocaleString('es-AR')} piezas disponibles${
                partido ? ` · salen ${cortePlural(partido)}` : n > 0 ? ' · el depósito define en cuántos capachos' : ''
              }`}
        </span>
      )}
      {error && <div style={aviso('err')}>{error}</div>}
    </div>
  );
}

/**
 * Qué capachos aceptados quedan armados al cerrar la orden. Va dentro del
 * diálogo de confirmar, que no se vuelve a dibujar: guarda lo tildado en su
 * propio estado y lo escribe en `elegidos`, que lee quien cierra.
 */
function ElegirArmados({ salidas, elegidos }: { salidas: SalidaDeOrden[]; elegidos: Set<string> }) {
  const [, refrescar] = useState(0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12.5, color: c.texto3 }}>Dejar armado para otra orden:</span>
      {salidas.map((s) => (
        <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={elegidos.has(s.id)}
            onChange={() => {
              if (!elegidos.delete(s.id)) elegidos.add(s.id);
              refrescar((n) => n + 1);
            }}
            style={{ width: 18, height: 18, flex: '0 0 18px' }}
          />
          <span style={{ fontFamily: mono, color: c.texto }}>{s.id}</span>
          <span>{s.piezas} pz</span>
        </label>
      ))}
    </div>
  );
}

/** Una salida de la orden, con el paso que le toca. */
function FilaDeSalida({
  i,
  s,
  e,
  ocupado,
  onAbrir,
  onMover,
  onUsarArmado,
  onSacar,
}: {
  i: number;
  s: SalidaDeOrden;
  e: EstadoEmision;
  ocupado: boolean;
  onAbrir: (id: string) => void;
  onMover: (id: string, estado: EstadoCapacho, item?: number) => void;
  onUsarArmado: (id: string, armado: string) => void;
  onSacar: (id: string) => void;
}) {
  const entero = s.unidad === 'pallet';
  const chico = { ...botonSecundario, padding: '7px 13px', fontSize: 13, opacity: ocupado ? 0.6 : 1 };
  const sinPallet = s.estado === 'Pedido' && s.candidatos.length === 0 && s.armados.length === 0;
  // En el celular `.wms-pedido` (global.css) apila el renglón en una tarjeta.
  // Los envoltorios de acá agrupan lo que va junto en esa tarjeta —el tilde con
  // el código, el estado con lo que lo acompaña, los dos botones lado a lado— y
  // en tablet y PC son `contents`: el renglón del escritorio queda igual.
  return (
    <motion.div
      layout
      {...fila(i)}
      // El rojo de «sin pallet» se prende y se apaga de a poco.
      animate={{
        ...fila(i).animate,
        backgroundColor: sinPallet ? c.rojoSuave : 'rgba(255,255,255,0)',
        boxShadow: `inset ${sinPallet ? 3 : 0}px 0 0 ${c.rojo}`,
      }}
      transition={{ ...fila(i).transition, layout: resorte, backgroundColor: suave, boxShadow: suave }}
      // En el celular, grilla: el código y su estado en el primer renglón y
      // todo lo demás abajo a lo ancho (como las filas del Inicio).
      className="wms-pedido flex max-tablet:grid max-tablet:grid-cols-[1fr_auto] max-tablet:[&>*]:col-[1/-1]"
      style={{
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderBottom: `1px solid ${c.borde}`,
        flexWrap: 'wrap',
      }}
    >
      {/* Se etiqueta lo que ya tiene pallet. El pallet que sale entero se va
          con la etiqueta que trae. */}
      <span className="flex items-center gap-3 tablet:contents max-tablet:col-[1]!">
        {!entero && s.pallet && !s.parteDe && <Tilde e={e} id={s.id} estado={s.estado} />}
        <span style={{ fontFamily: mono, fontSize: 13.5, fontWeight: 500, color: c.texto }}>{s.id}</span>
      </span>
      <span style={{ flex: 1, minWidth: 140, fontSize: 13, color: c.texto2 }}>
        {/* La parte es lo que le faltó a otro capacho: va en esa caja. */}
        {s.parteDe && `completa ${s.parteDe} · `}
        {entero ? 'pallet entero' : `${s.piezas} pz`}
        {s.pallet && (
          <>
            {' · de '}
            <motion.button
              layoutId={`pallet-${s.id}-${s.pallet}`}
              transition={resorte}
              type="button"
              onClick={() => onAbrir(s.pallet!)}
              title={`Abrir ${s.pallet}`}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                font: 'inherit',
                fontFamily: mono,
                color: c.verde,
                cursor: 'pointer',
                textDecoration: 'underline',
                // Sin la transición CSS de `transform` de los botones: Motion
                // mide la caja sacándole el transform, y si el CSS lo demora
                // mide cualquier cosa y el viaje se corta.
                transition: 'color 0.22s',
              }}
            >
              {s.pallet}
            </motion.button>
            {` · ${s.ubicacion ?? 'sin ubicar'}`}
          </>
        )}
      </span>
      {/* El chip es siempre el mismo: al aceptar o entregar cambia de color
          y de texto en el lugar (lo hace EstadoChip). */}
      <span className="self-center justify-self-end max-tablet:col-[2]! max-tablet:row-[1]!" style={{ display: 'inline-flex' }}>
        <EstadoChip estado={s.estado} />
      </span>
      {/* Vacío (sin armar ni etiqueta) no ocupa un renglón de la grilla. */}
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 empty:hidden tablet:contents">
        <AnimatePresence initial={false}>
          {s.armadoEn && (
            <motion.span key="armado" {...aparecer} style={{ fontSize: 12, color: c.texto3 }}>
              Armado · {s.armadoPor} · {horaOFecha(s.armadoEn)}
            </motion.span>
          )}
          {s.etiquetado && (
            <motion.span key="etiquetado" {...pop} style={{ display: 'inline-flex' }}>
              <Icono nombre="label" size={17} color={c.verde} />
            </motion.span>
          )}
        </AnimatePresence>
      </span>

      {/* Adónde ir a buscarlo: la **posición**, que es lo que el operario
          camina. El primero es el recomendado —el pallet que ya está abierto—
          y tocar uno acepta la salida contra ese pallet. */}
      {/* Los candidatos quedan fuera del AnimatePresence a propósito: un hijo
          que está saliendo no se vuelve a medir, y el botón tocado no podría
          viajar a ser el enlace del pallet. Se desmontan de una y la fila se
          achica con su `layout`. */}
      {s.estado === 'Pedido' && s.candidatos.length > 0 && (
        // Una columna en el celular: «C4-09 · N1 · abierto» no entra en media
        // pantalla y se partía en dos renglones dentro del botón.
        <motion.div initial={pop.initial} animate={pop.animate} transition={pop.transition} className="wms-candidatos max-tablet:grid-cols-1">
          <span style={{ fontSize: 12, color: c.texto3 }}>sale de</span>
          {s.candidatos.slice(0, 3).map((cand) => (
            // El que se toca se convierte en el enlace al pallet de la
            // descripción (mismo `layoutId`): viaja hasta ahí.
            <motion.button
              key={cand.pallet}
              layoutId={`pallet-${s.id}-${cand.pallet}`}
              transition={resorte}
              type="button"
              disabled={ocupado}
              onClick={() => onMover(s.id, 'Aceptado', cand.item)}
              title={
                entero
                  ? `${cand.pallet} · aceptarlo contra este pallet entero`
                  : cand.abierto
                  ? `${cand.pallet} · ya está abierto en la mesa y le quedan ${cand.saldo} piezas: seguí sacando de éste`
                  : `${cand.pallet} · aceptarlo contra éste, tiene ${cand.saldo} piezas`
              }
              style={{
                ...chico,
                fontFamily: mono,
                // Ver el enlace del pallet: el `transform` lo maneja Motion.
                transition: 'background-color 0.22s, border-color 0.22s, color 0.22s, opacity 0.22s',
                ...(cand.abierto ? { borderColor: c.verde, background: c.verdeSuave, color: c.verde } : {}),
              }}
            >
              {cand.ubicacion ?? 'sin ubicar'}
              {cand.abierto && <span style={{ fontFamily: 'inherit', fontSize: 12, opacity: 0.85 }}>· abierto</span>}
              {/* No le alcanza: pone lo que tiene y el resto sale de otro. */}
              {!entero && cand.saldo < s.piezas && (
                <span style={{ fontFamily: 'inherit', fontSize: 12, opacity: 0.85 }}>· {cand.saldo} pz</span>
              )}
            </motion.button>
          ))}
        </motion.div>
      )}
      {/* Un capacho que quedó armado de otra orden: tomarlo es aceptar con él. */}
      {s.estado === 'Pedido' && s.armados.length > 0 && (
        <motion.div initial={pop.initial} animate={pop.animate} transition={pop.transition} className="wms-candidatos max-tablet:grid-cols-1">
          <span style={{ fontSize: 12, color: c.texto3 }}>armado</span>
          {s.armados.slice(0, 3).map((a) => (
            <button
              key={a.id}
              type="button"
              disabled={ocupado}
              onClick={() => onUsarArmado(s.id, a.id)}
              title={`Usar ${a.id}, que quedó armado de otra orden con ${a.piezas} piezas, y entregarlo`}
              style={{ ...chico, fontFamily: mono }}
            >
              {a.id}
              {a.piezas < s.piezas && <span style={{ fontFamily: 'inherit', fontSize: 12, opacity: 0.85 }}>· {a.piezas} pz</span>}
            </button>
          ))}
        </motion.div>
      )}

      {/* Lo demás cambia con el estado: lo de antes sale y recién después
          entra lo nuevo (mode="wait"). En tablet y PC los envoltorios son
          `contents` y no se ven: por eso cada botón trae su propio `pop`. */}
      <AnimatePresence mode="wait" initial={false}>
      {sinPallet && (
          <motion.span key="sin-pallet" {...pop} className="max-tablet:self-start max-tablet:justify-self-start" style={{ display: 'inline-flex' }}>
            <EstadoChip estado={entero ? 'sin pallet entero libre' : 'sin pallet con saldo'} tono="err" />
          </motion.span>
      )}

      {/* Un paso para adelante y uno para atrás: el botón de más se corrige
          volviendo. */}
      {s.estado === 'Aceptado' && !s.parteDe && (
        <motion.div key="aceptado" {...pop} className="grid grid-cols-2 gap-2 tablet:contents">
          <motion.button {...pop} type="button" className="justify-center" disabled={ocupado} onClick={() => onMover(s.id, 'Pedido')} style={chico}>
            Volver a Pedido
          </motion.button>
          <motion.button
            {...pop}
            type="button"
            className="justify-center"
            disabled={ocupado}
            onClick={() => onMover(s.id, 'Entregado')}
            style={{ ...chico, borderColor: c.verdeBorde, color: c.verde }}
          >
            Entregar
          </motion.button>
          {/* Queda armado para otra orden; ésta lo vuelve a pedir. */}
          {!entero && (
            <motion.button {...pop} type="button" className="justify-center" disabled={ocupado} onClick={() => onSacar(s.id)} style={chico}>
              Sacar de la orden
            </motion.button>
          )}
        </motion.div>
      )}
      {s.estado === 'Entregado' && !s.parteDe && (
        <motion.button key="entregado" {...pop} type="button" className="justify-center" disabled={ocupado} onClick={() => onMover(s.id, 'Aceptado')} style={chico}>
          Deshacer entrega
        </motion.button>
      )}
      </AnimatePresence>
    </motion.div>
  );
}

/**
 * Todo lo que le pasó a este pallet, de lo último a lo primero.
 *
 * El historial de una posición cuenta lo que pasó en ese hueco; éste cuenta lo
 * que le pasó al pallet, que es la pregunta que se hace cuando falta algo: de
 * dónde salió, cuándo se ubicó, qué capachos se fueron y cuándo.
 */
/**
 * Admin: corregir cuántas piezas tiene arriba cada artículo del pallet, para
 * cuando la etiqueta se pegó en el que no era. Con motivo; queda en el historial.
 */
function CorregirCantidad({ pallet, items, onListo }: { pallet: string; items: LineaDePallet[]; onListo: () => void }) {
  const [valores, setValores] = useState<Record<number, string>>({});
  const [hecho, setHecho] = useState('');
  const [fallo, setFallo] = useState('');
  const [confirmar, dialogo] = useConfirmar();
  const actual = (it: LineaDePallet) => it.stockRdm ?? it.piezas;

  async function guardar(it: LineaDePallet) {
    const nuevo = Number(valores[it.id]);
    setHecho('');
    setFallo('');
    const motivo = await confirmar({
      titulo: `¿Corregir ${it.articulo} en ${pallet}?`,
      detalle: `Pasa de ${actual(it)} a ${nuevo} piezas. Queda registrado en el historial del pallet.`,
      boton: 'Corregir',
      motivo: 'Motivo',
    });
    if (motivo === null) return;
    try {
      await api.corregirPiezas(pallet, it.id, nuevo, motivo);
      setValores((v) => ({ ...v, [it.id]: '' }));
      setHecho(`${it.articulo} quedó con ${nuevo} piezas.`);
      onListo();
    } catch (err) {
      setFallo(mensajeDeError(err));
    }
  }

  // El diálogo va afuera de la tarjeta: el recorte de `abrir` lo encerraría
  // en ella (un `clip-path` también recorta a los hijos `fixed`).
  return (
    <>
    <motion.div {...abrir(16)} style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>Corregir cantidad</div>
      <div style={{ fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
        Para cuando la etiqueta se pegó en otro pallet: fijá las piezas que hay arriba de verdad. Lo entregado no se toca.
      </div>
      {items.map((it) => {
        const v = valores[it.id] ?? '';
        const valido = v !== '' && Number(v) >= 0 && Number(v) !== actual(it);
        return (
          <div key={it.id} style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <div className="min-w-0 flex-[1_1_200px] max-tablet:basis-full" style={{ fontSize: 13 }}>
              <span style={{ fontFamily: mono, color: c.texto }}>{it.articulo}</span>
              <span style={{ color: c.texto2 }}>
                {it.lote ? ` · ${it.lote}` : ''} · hoy <Numero valor={actual(it)} formato={String} /> pz
              </span>
            </div>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={v}
              placeholder={String(actual(it))}
              onChange={(e) => setValores((x) => ({ ...x, [it.id]: e.target.value }))}
              aria-label={`Piezas reales de ${it.articulo}`}
              // En el celular campo y botón se reparten el renglón.
              className="flex-1 tablet:flex-none tablet:w-[120px]"
              style={{ ...campo, width: undefined }}
            />
            <button
              type="button"
              onClick={() => void guardar(it)}
              disabled={!valido}
              style={{ ...botonSecundario, minHeight: 44, opacity: valido ? 1 : 0.5 }}
            >
              Corregir
            </button>
          </div>
        );
      })}
      <AnimatePresence>
        {hecho && (
          <motion.div key={`ok-${hecho}`} {...aparecer} role="status" style={aviso('ok')}>
            {hecho}
          </motion.div>
        )}
        {fallo && (
          <motion.div key={`err-${fallo}`} {...aparecer} role="alert" style={aviso('err')}>
            {fallo}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
    {dialogo}
    </>
  );
}

function HistorialDelPallet({ pallet }: { pallet: string }) {
  const [abierto, setAbierto] = useState(false);
  const { datos, cargando, error, recargar } = useApi(
    () => (abierto ? api.historialDePallet(pallet) : Promise.resolve(null)),
    [pallet, abierto],
  );
  const eventos = datos?.eventos ?? [];
  const pagina = usePaginado(eventos, 12);

  return (
    <motion.div {...abrir(16)} style={{ ...tarjeta, overflow: 'hidden' }}>
      <button
        type="button"
        className="wms-toca"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          border: 'none',
          background: 'none',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        <Icono nombre="history" size={19} color={c.texto3} />
        {/* En el celular «todo lo que le pasó» va debajo del título: al lado
            lo partía en dos renglones. Desde la tablet, el renglón de siempre. */}
        <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 tablet:contents">
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600, color: c.texto }}>Historial del pallet</span>
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={abierto ? 'ocultar' : 'ver'}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
            transition={suave}
            style={{ fontSize: 12.5, color: c.texto3 }}
          >
            {abierto ? 'ocultar' : 'todo lo que le pasó'}
          </motion.span>
        </AnimatePresence>
        </span>
        {/* La flecha gira en vez de cambiar de icono. */}
        <motion.span initial={false} animate={{ rotate: abierto ? 180 : 0 }} transition={suave} style={{ display: 'grid' }}>
          <Icono nombre="expand_more" size={20} color={c.texto3} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
      {abierto && (
        <motion.div key="historial" {...desplegar}>
        {error ? (
          <PanelError mensaje={error} onReintentar={recargar} />
        ) : cargando && eventos.length === 0 ? (
          <Cargando texto="Buscando lo que pasó…" />
        ) : (
          <>
            {/* Escalonados al abrir y al pasar de página (12 por página). */}
            {pagina.visibles.map((e, i) => {
              const t = tonoDeTexto(e.tono);
              return (
                <motion.div
                  key={e.id}
                  {...fila(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 16px',
                    borderTop: `1px solid ${c.borde}`,
                  }}
                >
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      flex: '0 0 32px',
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      background: tono(t).bg,
                      border: `1px solid ${tono(t).bd}`,
                      color: tono(t).fg,
                    }}
                  >
                    <Icono nombre={e.icono} size={17} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 13.5, color: c.texto, textWrap: 'pretty' }}>{e.detalle}</span>
                    <span style={{ fontSize: 12, color: c.texto4 }}>
                      {e.tipo}
                      {e.posicion ? ` · ${e.posicion}` : ''}
                      {e.operario ? ` · ${e.operario}` : ''}
                      {/* En el celular la fecha va al final de este renglón: como
                          columna a la derecha dejaba el detalle en 180 px. */}
                      <span className="tablet:hidden" style={{ color: c.texto3 }}> · {e.fecha}</span>
                    </span>
                  </span>
                  <span className="max-tablet:hidden" style={{ fontSize: 12, color: c.texto3, whiteSpace: 'nowrap' }}>{e.fecha}</span>
                </motion.div>
              );
            })}

            <Paginador
              pagina={pagina.pagina}
              paginas={pagina.paginas}
              total={pagina.total}
              desde={pagina.desde}
              porPagina={12}
              que="movimientos"
              onIr={pagina.setPagina}
            />
          </>
        )}
        </motion.div>
      )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Fraccionamiento({
  pallet,
  onAbrir,
  onVolver,
  onIr,
}: {
  pallet: string | null;
  onAbrir: (id: string) => void;
  onVolver: () => void;
  onIr: (v: Vista, id?: string) => void;
}) {
  // La misma ruta lleva las dos cosas: un pallet abre su ficha, y una orden
  // —desde la mesa o desde Inicio— abre la suya, que es donde se completa.
  if (!pallet) return <Ordenes onAbrir={onAbrir} />;
  if (/^OT-/i.test(pallet))
    return <DetalleOrden orden={pallet.toUpperCase()} onAbrir={onAbrir} onVolver={onVolver} onIr={onIr} />;
  return <Detalle pallet={pallet} onVolver={onVolver} onIr={onIr} />;
}
